import type { EdmxEnumType } from '@1c-odata/metadata'
import { compareCyrillic } from '../util.js'

const HEADER = '// Auto-generated by @1c-odata/cli. DO NOT EDIT.'

// TS identifier rule (ES2015+): ID_Start followed by ID_Continue / $ / _.
// Cyrillic letters qualify; hyphens, spaces, leading digits do not.
const TS_IDENT_RE = /^[\p{ID_Start}_$][\p{ID_Continue}_$‌‍]*$/u

/**
 * Escape backslashes and single quotes for safe inclusion inside a single-quoted
 * TS string literal (used both for object key and value).
 */
function escapeSingleQuoted(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/'/g, "\\'")
}

/**
 * Result of `emitEnumsFile`: emitted TS source plus a list of warnings
 * (skipped empty enums, deduped names). Caller decides how to surface them —
 * the coordinator forwards to stderr; library / test callers can inspect
 * the array directly without touching process state.
 */
export interface EmitEnumsResult {
  code: string
  warnings: string[]
}

/**
 * Emit a `generated/<connection>/enums.ts` body containing one `as const`
 * object + type alias per non-empty EDMX EnumType.
 *
 * Output is deterministic: enums sorted by name (Cyrillic-aware), members
 * preserve their EDMX order (implicit 0-indexed mapping).
 *
 * Pure function — no side effects. Warnings are returned alongside the
 * emitted code; the coordinator forwards them to stderr.
 *
 * Empty enums (no members) are skipped with a warning — emitting an
 * empty object is useless for consumers and a `type X = never` alias is
 * actively misleading.
 *
 * Throws if an enum name is not a valid TypeScript identifier. In practice
 * 1C EDMX produces Cyrillic identifier-safe names; this guards future-self.
 */
export function emitEnumsFile(enums: readonly EdmxEnumType[]): EmitEnumsResult {
  const warnings: string[] = []
  const seen = new Set<string>()
  const usable: EdmxEnumType[] = []
  for (const e of enums) {
    if (e.members.length === 0) {
      warnings.push(`skipping EnumType "${e.name}" — zero members`)
      continue
    }
    if (!TS_IDENT_RE.test(e.name)) {
      throw new Error(
        `EnumType "${e.name}" has an invalid TypeScript identifier name; cannot emit. ` +
          `Open an issue if you hit this — 1C EDMX is expected to produce ident-safe names.`,
      )
    }
    if (seen.has(e.name)) {
      warnings.push(`skipping duplicate EnumType "${e.name}" — keeping first occurrence`)
      continue
    }
    seen.add(e.name)
    usable.push(e)
  }
  if (usable.length === 0) {
    return { code: `${HEADER}\nexport {}\n`, warnings }
  }
  const sorted = [...usable].sort((a, b) => compareCyrillic(a.name, b.name))
  const blocks = sorted.map((e) => {
    const memberLines = e.members
      .map((m) => `  '${escapeSingleQuoted(m.name)}': '${escapeSingleQuoted(m.name)}',`)
      .join('\n')
    return (
      `export const ${e.name} = {\n${memberLines}\n} as const\n` +
      `export type ${e.name} =\n  (typeof ${e.name})[keyof typeof ${e.name}]`
    )
  })
  return { code: `${HEADER}\n${blocks.join('\n\n')}\n`, warnings }
}
